import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import NoteIcon from '@mui/icons-material/Note';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import "../../css/ProposalCard.css";

const endpointpath = process.env.REACT_APP_API_BASE_URL;
const SUMMARY_PREVIEW_LIMIT = 180;
const EXPERIENCE_PREVIEW_LIMIT = 160;

/** DynamoDB / API may occasionally return maps; never render raw objects as React children. */
const coercePlainText = (v) => {
  if (v == null || v === '') return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return '';
    }
  }
  return String(v);
};

const ProposalCard = ({ proposal, proposals, fetchProposals, isExpanded, onToggleExpand }) => {
  const [openDialog, setDialog] = useState(false);
  const [actionType, setActionType] = useState(null);  // To track Accept/Reject action
  const [acceptAndRejectOthers, setAcceptAndRejectOthers] = useState(false);  // For "Accept & Reject Others" checkbox

  const handleAccept = () => {
    setActionType('accept');
    setDialog(true);
  };

  const handleReject = () => {
    setActionType('reject');
    setDialog(true);
  };

  const closeDialog = () => {
    setDialog(false);
    setAcceptAndRejectOthers(false); // Reset checkbox state
  };

  const handleConfirmAction = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log("No token found in localStorage.");
        return;
      }

      // Action for the current proposal
      const currentActionBody = {
        proposalID: proposal.proposalID,
        applicationStatus: actionType === 'accept' ? 'Accepted' : 'Rejected',
        // Email Metadata added here
        applicantEmail: proposal.applicantEmail,
        applicantName: proposal.applicantName,
        projectTitle: proposal.proposalTitle,
      };

      const response = await fetch(`${endpointpath}/api/applicationStatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'authorization': token,
        },
        body: JSON.stringify(currentActionBody),
      });
      if (!response.ok) {
        const errText = await response.text();
        console.error('Failed to update proposal status:', response.status, errText);
        alert(`Status was updated but notification email may have failed: ${errText}`);
        return;
      }

      // Handling "Reject All Others" logic
      if (actionType === 'accept' && acceptAndRejectOthers) {
        const rejectOthers = proposals
          .filter((p) => p.proposalID !== proposal.proposalID && p.applicationStatus === 'In Review')
          .map((otherProposal) => {
            return fetch(`${endpointpath}/api/applicationStatus`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'authorization': token,
              },
              body: JSON.stringify({
                proposalID: otherProposal.proposalID,
                applicationStatus: 'Rejected',
                // Metadata for other applicants being rejected
                applicantEmail: otherProposal.applicantEmail,
                applicantName: otherProposal.applicantName,
                projectTitle: otherProposal.proposalTitle,
              }),
            });
          });

        await Promise.all(rejectOthers);
      }

      // Fetch updated proposals after actions
      fetchProposals(proposal.projectID);
      closeDialog();
    } catch (err) {
      console.error('Error updating proposal status:', err);
    }
  };

  const summary = coercePlainText(proposal.proposalSummary ?? proposal.summary ?? '');
  const experience = coercePlainText(proposal.experience ?? proposal.background ?? '');
  const degreeProgram = coercePlainText(proposal.degreeProgram ?? proposal.degree ?? '');
  const anticipatedOutcomes = coercePlainText(proposal.anticipatedOutcomes ?? proposal.expectedOutcomes ?? '');
  const unmetRequirements = coercePlainText(proposal.unmetRequirements ?? '');
  const asHttpUrl = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');
  const resumeUrl = asHttpUrl(proposal.resumeUpload ?? proposal.resumeUrl ?? '');
  const supplementalUrl = asHttpUrl(
    proposal.supplementalUpload ?? proposal.supplementalfileUrl ?? proposal.supplementalUrl ?? ''
  );

  const summarize = (text, limit = SUMMARY_PREVIEW_LIMIT) => {
    const s = coercePlainText(text);
    if (!s) return 'Not provided.';
    return s.length > limit ? `${s.slice(0, limit)}…` : s;
  };

  const renderField = (label, value) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          color: '#4a1711',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
        {coercePlainText(value) || 'Not provided.'}
      </Typography>
    </Box>
  );

  const renderStatusChip = () => {
    if (proposal.applicationStatus === 'Accepted') {
      return <Typography sx={{ color: '#0b9444', fontWeight: 600 }}>Proposal Accepted</Typography>;
    }
    if (proposal.applicationStatus === 'Rejected') {
      return <Typography sx={{ color: '#c62828', fontWeight: 600 }}>Proposal Rejected</Typography>;
    }
    return null;
  };

  const summaryPreview = summarize(summary, SUMMARY_PREVIEW_LIMIT);
  const experiencePreview = summarize(experience, EXPERIENCE_PREVIEW_LIMIT);
  const showFullSummary = summary && summary.length > SUMMARY_PREVIEW_LIMIT;
  const showFullExperience = experience && experience.length > EXPERIENCE_PREVIEW_LIMIT;

  return (
    <>
      <Card className="proposal-card" onClick={onToggleExpand}>
        <CardContent className="proposal-card-header">
          <Box display="flex" flexDirection="column" marginTop={1} sx={{ gap: 0.5 }}>
            <Typography variant="h6">{coercePlainText(proposal.proposalTitle) || 'Untitled Proposal'}</Typography>
            <Typography variant="body1">
              Submitted By: {coercePlainText(proposal.applicantName) || 'N/A'}
            </Typography>
            <Typography variant="body2">
              Summary: {summaryPreview}
            </Typography>
            <Typography variant="body2">
              Experience: {experiencePreview}
            </Typography>
          </Box>
          <Box
            className="proposal-attachments"
            onClick={(e) => e.stopPropagation()}
            role="group"
            aria-label="Proposal attachments"
          >
            <Tooltip title={resumeUrl ? 'Open resume' : 'Resume unavailable'}>
              <span>
                <IconButton
                  className="attachment-icon"
                  component={resumeUrl ? 'a' : 'button'}
                  href={resumeUrl || undefined}
                  target={resumeUrl ? '_blank' : undefined}
                  rel={resumeUrl ? 'noopener noreferrer' : undefined}
                  disabled={!resumeUrl}
                  aria-label="Resume attachment"
                >
                  <NoteIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={supplementalUrl ? 'Open supplemental file' : 'Supplemental file unavailable'}
            >
              <span>
                <IconButton
                  className="attachment-icon"
                  component={supplementalUrl ? 'a' : 'button'}
                  href={supplementalUrl || undefined}
                  target={supplementalUrl ? '_blank' : undefined}
                  rel={supplementalUrl ? 'noopener noreferrer' : undefined}
                  disabled={!supplementalUrl}
                  aria-label="Supplemental attachment"
                >
                  <AttachFileIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </CardContent>

        {isExpanded && (
          <CardContent className="proposal-card-body">
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {renderField('Degree Program & Department', degreeProgram)}
              {showFullSummary && renderField('Full Proposal Summary', summary)}
              {showFullExperience && renderField('Detailed Experience & Background', experience)}
              {renderField('Anticipated Outcomes', anticipatedOutcomes)}
              {proposal.ableToMeetRequirements === 'YES'
                ? renderField('Able to Meet Requirements', 'Yes')
                : renderField(
                    'Requirements Unmet',
                    unmetRequirements || 'Indicated some requirements need clarification.'
                  )}
            </Box>

            <Box className="proposal-action-buttons">
              {proposal.applicationStatus === 'In Review' ? (
                <>
                  <Button
                    onClick={handleAccept}
                    sx={{
                      backgroundColor: '#0b9444',
                      color: '#ffffff',
                      fontWeight: 600,
                      minWidth: 150,
                      '&:hover': { backgroundColor: '#086c31' },
                    }}
                  >
                    Accept
                  </Button>
                  <Button
                    onClick={handleReject}
                    sx={{
                      backgroundColor: '#c62828',
                      color: '#ffffff',
                      fontWeight: 600,
                      minWidth: 150,
                      '&:hover': { backgroundColor: '#8e0000' },
                    }}
                  >
                    Reject
                  </Button>
                </>
              ) : (
                renderStatusChip()
              )}
            </Box>
          </CardContent>
        )}
      </Card>

      <Dialog open={openDialog} onClose={closeDialog}>
        <DialogTitle>
          {actionType === 'accept' ? 'Confirm Proposal Acceptance' : 'Confirm Proposal Rejection'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {actionType === 'accept'
              ? 'Are you sure you want to accept this proposal? You can also choose to reject all other proposals for this project.'
              : 'Are you sure you want to reject this proposal?'}
          </DialogContentText>

          {actionType === 'accept' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptAndRejectOthers}
                  onChange={(e) => setAcceptAndRejectOthers(e.target.checked)}
                  color="primary"
                />
              }
              label="Accept & Reject All Others"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="primary" autoFocus>
            {actionType === 'accept' ? 'Accept' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProposalCard;
